import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import JSZip from 'jszip';
import { Download, ImageIcon, Loader2, Square, X } from 'lucide-react';

import { Button, Modal } from './ui';
import { cn } from '../lib/cn';
import { imagesApi } from '../services/annotatorApi';
import { renderAnnotatedImage, renderAnnotatedImageToBlob } from '../utils/renderAnnotatedImage';
import type { Class as ClassModel, ImageMeta } from '../types/api';

/**
 * DownloadImagesModal — single shared component for both per-image and
 * bulk image downloads. The caller decides scope by what it puts in the
 * `images` array:
 *
 *   • 1 image  → single-image mode: copy reads "this image", annotated
 *                path downloads ONE PNG directly via renderAnnotatedImage
 *                (no JSZip overhead, no progress modal).
 *   • N images → bulk mode: annotated path renders each PNG client-side,
 *                packs into a JSZip, downloads one .zip. Progress modal
 *                only mounts after 500ms (small projects don't flash).
 *
 * Raw option in both modes uses server-streamed bytes — bulk hits the
 * /images/download endpoint, single hits /images/:id/bytes.
 */
interface DownloadImagesModalProps {
    open: boolean;
    onClose: () => void;
    isDarkMode?: boolean;
    /** Images to be offered for download. Length 1 = single mode. */
    images: ImageMeta[];
    classes: ReadonlyArray<ClassModel>;
    /** Required for bulk-raw — the server-streamed ZIP endpoint is
     *  project-scoped. Single-raw uses the per-image bytes URL. */
    projectId: string;
    orgId?: string;
}

const ANNOTATED_RENDER_CAP = 500;
const PROGRESS_REVEAL_MS = 500;

export default function DownloadImagesModal({
    open,
    onClose,
    isDarkMode = false,
    images,
    classes,
    projectId,
    orgId,
}: DownloadImagesModalProps) {
    const { t } = useTranslation('canvas');
    const { t: tCommon } = useTranslation('common');
    const { t: tErrors } = useTranslation('errors');

    const isSingle = images.length === 1;
    const single = isSingle ? images[0] : null;
    const annotatedImages = images.filter((i) => i.status === 'annotated');

    const [phase, setPhase] = useState<'choose' | 'rendering'>('choose');
    const [done, setDone] = useState(0);
    const [total, setTotal] = useState(0);
    const abortRef = useRef<{ cancelled: boolean }>({ cancelled: false });
    const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!open) {
            setPhase('choose');
            setDone(0);
            setTotal(0);
            abortRef.current = { cancelled: false };
            if (revealTimerRef.current) {
                clearTimeout(revealTimerRef.current);
                revealTimerRef.current = null;
            }
        }
    }, [open]);

    function downloadRaw() {
        if (isSingle && single) {
            window.location.href = imagesApi.bytesUrl(single.id, orgId);
        } else {
            window.location.href = imagesApi.downloadAllUrl(projectId, orgId);
        }
        onClose();
    }

    async function downloadAnnotated() {
        if (annotatedImages.length === 0) {
            toast.error(t('no_annotated_images', { defaultValue: 'No annotated images to render' }));
            return;
        }

        // Single image: direct render + download. No JSZip, no progress
        // modal — the PNG is ready in ~200ms and the browser save dialog
        // is the user's confirmation.
        if (annotatedImages.length === 1) {
            try {
                await renderAnnotatedImage({
                    imageId: annotatedImages[0].id,
                    orgId,
                    filename: annotatedImages[0].filename,
                    classes,
                });
                onClose();
            } catch (err) {
                toast.error(String((err as Error)?.message ?? tErrors('generic')));
            }
            return;
        }

        // Bulk: cap + JSZip + delayed progress modal.
        if (annotatedImages.length > ANNOTATED_RENDER_CAP) {
            toast.error(t('annotated_cap_exceeded', {
                defaultValue: 'Too many images to render in-browser ({{count}}). Cap is {{cap}}.',
                count: annotatedImages.length,
                cap: ANNOTATED_RENDER_CAP,
            }));
            return;
        }

        setTotal(annotatedImages.length);
        setDone(0);
        abortRef.current = { cancelled: false };
        if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
        revealTimerRef.current = setTimeout(() => setPhase('rendering'), PROGRESS_REVEAL_MS);

        const zip = new JSZip();
        const folder = zip.folder('annotated') ?? zip;
        let failedCount = 0;

        try {
            for (let i = 0; i < annotatedImages.length; i++) {
                if (abortRef.current.cancelled) throw new Error('aborted');
                const img = annotatedImages[i];
                try {
                    const blob = await renderAnnotatedImageToBlob({
                        imageId: img.id,
                        orgId,
                        classes,
                    });
                    const safeName = img.filename.replace(/\.[^.]+$/, '') + '_annotated.png';
                    folder.file(safeName, blob);
                } catch {
                    failedCount += 1;
                }
                setDone(i + 1);
            }

            if (abortRef.current.cancelled) throw new Error('aborted');

            if (revealTimerRef.current) {
                clearTimeout(revealTimerRef.current);
                revealTimerRef.current = null;
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const url = URL.createObjectURL(zipBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `project_${projectId.slice(0, 8)}_annotated.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            if (failedCount > 0) {
                toast(t('annotated_render_partial', {
                    defaultValue: 'Downloaded with {{count}} render failures',
                    count: failedCount,
                }), { icon: '⚠️' });
            }
            onClose();
        } catch (err) {
            if (revealTimerRef.current) {
                clearTimeout(revealTimerRef.current);
                revealTimerRef.current = null;
            }
            if ((err as Error)?.message !== 'aborted') {
                toast.error(tErrors('generic'));
            }
            setPhase('choose');
        }
    }

    function cancelRender() {
        abortRef.current.cancelled = true;
    }

    const optionRowCls = cn(
        'w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
        isDarkMode
            ? 'border-gray-700 hover:border-primary-500 hover:bg-gray-800/40'
            : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/40',
    );
    const iconWrapCls = cn(
        'h-9 w-9 shrink-0 rounded-md flex items-center justify-center',
        isDarkMode ? 'bg-primary-500/15 text-primary-300' : 'bg-primary-50 text-primary-600',
    );

    const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;
    const annotatedDisabled = annotatedImages.length === 0;

    const titleChoose = isSingle
        ? t('download_image_title', { defaultValue: 'Download image' })
        : t('download_all_title', { defaultValue: 'Download all images' });
    const titleRendering = t('rendering_title', { defaultValue: 'Rendering annotated images…' });

    const annotatedLabel = t('download_annotated', { defaultValue: 'With annotations' });
    const annotatedSub = isSingle
        ? (annotatedDisabled
            ? t('download_annotated_empty_single', { defaultValue: 'No boxes drawn on this image yet.' })
            : t('download_annotated_sub', { defaultValue: 'PNG with bounding boxes drawn — for review only, not for training.' }))
        : (annotatedDisabled
            ? t('download_all_annotated_empty', { defaultValue: 'No annotated images yet — draw boxes first.' })
            : t('download_all_annotated_sub', {
                defaultValue: 'ZIP of {{count}} PNGs with bounding boxes drawn — for review only, not for training.',
                count: annotatedImages.length,
            }));

    const rawLabel = isSingle
        ? t('download_original', { defaultValue: 'Original' })
        : t('download_all_original', { defaultValue: 'Originals' });
    const rawSub = isSingle
        ? t('download_original_sub', { defaultValue: 'Raw bytes as uploaded — same file that goes into the YOLO snapshot.' })
        : t('download_all_original_sub', {
            defaultValue: 'ZIP of all {{count}} raw image files. Streamed from the server.',
            count: images.length,
        });

    return (
        <Modal
            open={open}
            onClose={phase === 'rendering' ? () => {} : onClose}
            title={phase === 'rendering' ? titleRendering : titleChoose}
            widthClass="max-w-md"
            isDarkMode={isDarkMode}
            footer={
                phase === 'rendering' ? (
                    <Button
                        variant="ghost"
                        isDarkMode={isDarkMode}
                        leftIcon={<X className="h-4 w-4" />}
                        onClick={cancelRender}
                    >
                        {tCommon('cancel')}
                    </Button>
                ) : (
                    <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose}>
                        {tCommon('cancel')}
                    </Button>
                )
            }
        >
            {phase === 'rendering' ? (
                <div className="space-y-3">
                    <div className="flex items-center gap-3">
                        <Loader2 className={cn('h-5 w-5 animate-spin', isDarkMode ? 'text-primary-400' : 'text-primary-600')} />
                        <p className={cn('text-sm font-medium', isDarkMode ? 'text-gray-200' : 'text-gray-800')}>
                            {t('rendering_progress', {
                                defaultValue: '{{done}} of {{total}} ({{pct}}%)',
                                done,
                                total,
                                pct: progressPct,
                            })}
                        </p>
                    </div>
                    <div className={cn('h-2 w-full rounded-full overflow-hidden', isDarkMode ? 'bg-gray-800' : 'bg-gray-200')}>
                        <div
                            className={cn('h-full transition-all duration-150', isDarkMode ? 'bg-primary-400' : 'bg-primary-600')}
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                    <p className={cn('text-xs', isDarkMode ? 'text-gray-500' : 'text-gray-500')}>
                        {t('rendering_sub', { defaultValue: 'Hang tight — the ZIP starts downloading as soon as the last image is rendered.' })}
                    </p>
                </div>
            ) : (
                <>
                    {isSingle && single && (
                        <p className={cn('text-sm mb-4 truncate', isDarkMode ? 'text-gray-400' : 'text-gray-600')}>
                            {single.filename}
                        </p>
                    )}
                    <div className="space-y-2">
                        <button
                            type="button"
                            onClick={() => void downloadAnnotated()}
                            disabled={annotatedDisabled}
                            className={cn(optionRowCls, annotatedDisabled && 'opacity-50 cursor-not-allowed hover:border-gray-200')}
                        >
                            <span className={iconWrapCls}>
                                <Square className="h-4 w-4" />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className={cn('block text-sm font-semibold', isDarkMode ? 'text-gray-100' : 'text-gray-900')}>
                                    {annotatedLabel}
                                </span>
                                <span className={cn('block text-xs mt-0.5', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>
                                    {annotatedSub}
                                </span>
                            </span>
                            <Download className={cn('h-4 w-4 shrink-0 mt-1', isDarkMode ? 'text-gray-400' : 'text-gray-500')} />
                        </button>
                        <button
                            type="button"
                            onClick={downloadRaw}
                            className={optionRowCls}
                        >
                            <span className={iconWrapCls}>
                                <ImageIcon className="h-4 w-4" />
                            </span>
                            <span className="flex-1 min-w-0">
                                <span className={cn('block text-sm font-semibold', isDarkMode ? 'text-gray-100' : 'text-gray-900')}>
                                    {rawLabel}
                                </span>
                                <span className={cn('block text-xs mt-0.5', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>
                                    {rawSub}
                                </span>
                            </span>
                            <Download className={cn('h-4 w-4 shrink-0 mt-1', isDarkMode ? 'text-gray-400' : 'text-gray-500')} />
                        </button>
                    </div>
                </>
            )}
        </Modal>
    );
}
